import { API } from '@aws-amplify/api'
import { useQuery, useMutation, UseMutationResult } from 'react-query'
import * as Sentry from '@sentry/nextjs'

import useAuth from '../../hooks/useAuth'

export type BuyingPower = {
  isMinimumAcreBudget: boolean
  qualificationFailures: string[]
  homePrice: number
  monthlyPayment: number
  termWealth: {
    saver: number
  }
  maxApprovedAmount?: number
}

export enum PrequalificationTypes {
  buildYourBudget,
  mortgageCompare,
}

export type MortgageComparePrequalification = {
  targetHomePrice: number
  customRate: number
  emailAddress?: string
  saverMonthlySavings?: number
  boostThreeYearsEstimatedWealthIncrease?: number
}

export type BudgetPrequalification = {
  targetHomePrice: number
  selfReportedAnnualIncome: number
  selfReportedCreditRange: {
    min: number | undefined
    max: number | undefined
  }
  buyingPower: BuyingPower
  generatedTimestamp: number
}

export type Prequalification =
  | {
      type: PrequalificationTypes.buildYourBudget
      details: BudgetPrequalification
    }
  | {
      type: PrequalificationTypes.mortgageCompare
      details: MortgageComparePrequalification
    }

const loadPrequalification = async (): Promise<Prequalification | null> => {
  try {
    const response = await API.get('customer', '/prequalification', {})

    return response.prequalification

    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  } catch (error: any) {
    Sentry.captureException(error)
    throw new Error(error.response?.data?.message || 'Something went wrong')
  }
}

const savePrequalification = async (
  prequalification: Prequalification,
  emailAddress?: string
): Promise<void> => {
  try {
    if (emailAddress) {
      await API.post('customer', '/unauth/prequalification', {
        queryStringParameters: {
          email: emailAddress,
        },
        body: { prequalification },
      })
    } else {
      await API.post('customer', '/prequalification', {
        body: { prequalification },
      })
    }

    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  } catch (error: any) {
    Sentry.captureException(error)
    throw new Error(error.response?.data?.message || 'Something went wrong')
  }
}

export const usePrequalification = () => {
  const { isAuthenticated } = useAuth()

  return useQuery('prequalification', loadPrequalification, {
    enabled: isAuthenticated,
  })
}

export type SavePrequalificationParams = {
  prequalification: Prequalification
  emailAddress?: string
}

export const useSavePrequalification = (): UseMutationResult<
  void,
  Error,
  SavePrequalificationParams,
  unknown
> => {
  return useMutation(
    'savePrequalification',
    (params: SavePrequalificationParams) =>
      savePrequalification(params.prequalification, params.emailAddress)
  )
}
